import React, { useState } from "react";

import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import toast from "components/ui/toast/Toast";
import { copyToClipboard } from "lib/clipboard";
import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventDataTypes, EventNames, useTracking } from "../../lib/tracking";
import { LeadEntityType } from "../../lib/types/graphQLEnums";
import SaveListDrawer from "../entities/save_components/lists/SaveListDrawer";
import SaveLeadDrawer from "../leads/saving/SaveLeadDrawer";
import { buildNoticeShareDetails } from "./noticeShareDetails";

type Props = {
  onShareClick: () => void;
  notice: Omit<NoticeDetails, "suppliers"> & {
    suppliers: string[];
  };
};

export function NoticeHeaderDropdown({ onShareClick, notice }: Props) {
  const currentUser = useAssertCurrentUser();

  const [isLeadDrawerOpen, setIsLeadDrawerOpen] = useState(false);
  const [isListDrawerOpen, setIsListDrawerOpen] = useState(false);

  const { logEvent } = useTracking();

  const onClickCopyUrl = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const noticeUrl = window.location.origin + `/notices/${notice.guid}`;
    const copied = await copyToClipboard({ plain: noticeUrl });

    copied ? toast.success("Copied URL") : toast.error("Failed to copy URL");
  };

  const onClickCopyDetails = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const noticeUrl = window.location.origin + `/notices/${notice.guid}`;
    const copied = await copyToClipboard(buildNoticeShareDetails(notice, noticeUrl));

    if (copied) {
      logEvent(EventNames.noticeDetailsCopied, { "Record GUIDs": [notice.guid] });
      toast.success("Copied notice details");
    } else {
      toast.error("Failed to copy notice details");
    }
  };

  const onClickAddToList = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    logEvent(EventNames.saveDataClicked, {
      "Context source": "Header",
      "Data type": EventDataTypes.notices,
    });

    setIsListDrawerOpen(true);
  };

  return (
    <>
      <DropdownMenu
        trigger={
          <DropdownMenuTrigger
            buttonStyle="inverse"
            icon="dotsHorizontal"
            hideArrow
            buttonType="primary"
          />
        }
        align="end"
        width={170}
        modal={false}
      >
        <DropdownMenuItem
          key="save"
          onClick={() => setIsLeadDrawerOpen(true)}
          icon="bookmark"
          iconProps={{
            color: "inherit",
            fill: "sysTextWhite",
          }}
        >
          Save to Lead
        </DropdownMenuItem>
        <DropdownMenuItem key="share" onClick={onShareClick} icon="share07">
          Share by email
        </DropdownMenuItem>
        <DropdownMenuItem key="copy-details" onClick={onClickCopyDetails} icon="copy01">
          Copy details
        </DropdownMenuItem>
        <DropdownMenuItem key="copy-url" onClick={onClickCopyUrl} icon="link01">
          Copy URL
        </DropdownMenuItem>

        <DropdownMenuItem key="save-list" onClick={onClickAddToList} icon="plus">
          Add to list
        </DropdownMenuItem>
      </DropdownMenu>
      <SaveLeadDrawer
        entityId={notice.procurementStage.id}
        entityType={LeadEntityType.Notice}
        isOpen={isLeadDrawerOpen}
        onOpenChange={setIsLeadDrawerOpen}
        savedLeadIds={notice.leads?.map((lead) => lead.id) || []}
        trackingData={{
          "Context source": "Notice header",
        }}
        defaultValues={{
          name: notice.name ?? undefined,
          value: notice.value ?? undefined,
          assignedToId: currentUser.guid,
          expiryDate: notice.expiryDate ?? undefined,
          description: `Lead created from [${notice.name ?? "this notice"}](/notices/${notice.guid})`,
          entities: [
            {
              entityId: notice.procurementStage.id,
              entityType: LeadEntityType.Notice,
            },
            ...notice.buyers.map((buyer) => ({
              entityId: buyer.guid,
              entityType: LeadEntityType.Buyer,
            })),
            ...notice.suppliers.map((id) => ({
              entityId: id,
              entityType: LeadEntityType.Supplier,
            })),
          ],
          noticeStage: notice.procurementStage.stage ?? undefined,
          stage: notice.stage ?? undefined,
        }}
      />
      <SaveListDrawer
        isOpen={isListDrawerOpen}
        onOpenChange={setIsListDrawerOpen}
        entityType="notice"
        entityIds={[notice.guid]}
      />
    </>
  );
}
